import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardLayout from './components/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import ConversationsPage from './pages/ConversationsPage';
import ConversationDetailPage from './pages/ConversationDetailPage';
import ReservationsPage from './pages/ReservationsPage';
import CalendarPage from './pages/CalendarPage';
import SettingsPage from './pages/SettingsPage';
import ReportsPage from './pages/ReportsPage';
import BotControlPage from './pages/BotControlPage';
import SuperAdminLayout from './components/SuperAdminLayout';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import SuperAdminHotels from './pages/superadmin/SuperAdminHotels';
import SuperAdminHotelDetail from './pages/superadmin/SuperAdminHotelDetail';
import SuperAdminUsers from './pages/superadmin/SuperAdminUsers';
import SuperAdminLogs from './pages/superadmin/SuperAdminLogs';
import SuperAdminSignups from './pages/superadmin/SuperAdminSignups';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminCustomerDetail from './pages/admin/AdminCustomerDetail';
import AdminDaily from './pages/admin/AdminDaily';
import AdminSignups from './pages/admin/AdminSignups';
import AdminDemoRequests from './pages/admin/AdminDemoRequests';
import LandingPage from './pages/LandingPage';
import NotFoundPage from './pages/NotFoundPage';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#64748b' }}>Yükleniyor...</div>;
  if (!user) return <Navigate to="/landing" />;
  if (user.role === 'SUPERADMIN') return <Navigate to="/superadmin" />;
  if (user.role === 'SUPER_ADMIN') return <Navigate to="/admin" />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#64748b' }}>Yükleniyor...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'SUPERADMIN') return <Navigate to="/" />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#64748b' }}>Yükleniyor...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'SUPER_ADMIN') return <Navigate to="/" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role === 'SUPERADMIN') return <Navigate to="/superadmin" />;
  if (user?.role === 'SUPER_ADMIN') return <Navigate to="/admin" />;
  return user ? <Navigate to="/" /> : children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/" element={<PrivateRoute><SocketProvider><DashboardLayout /></SocketProvider></PrivateRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="conversations" element={<ConversationsPage />} />
        <Route path="conversations/:id" element={<ConversationDetailPage />} />
        <Route path="reservations" element={<ReservationsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="bot-control" element={<BotControlPage />} />
      </Route>

      {/* Superadmin panel (eski — SUPERADMIN rolü) */}
      <Route path="/superadmin" element={<SuperAdminRoute><SuperAdminLayout /></SuperAdminRoute>}>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="hotels" element={<SuperAdminHotels />} />
        <Route path="hotels/:id" element={<SuperAdminHotelDetail />} />
        <Route path="users" element={<SuperAdminUsers />} />
        <Route path="logs" element={<SuperAdminLogs />} />
        <Route path="signups" element={<SuperAdminSignups />} />
      </Route>

      {/* Admin panel (yeni — SUPER_ADMIN rolü) */}
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="customers/:id" element={<AdminCustomerDetail />} />
        <Route path="signups" element={<AdminSignups />} />
        <Route path="daily" element={<AdminDaily />} />
        <Route path="demo-requests" element={<AdminDemoRequests />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
